/**
 * Cart Reward Ladder — free-gift selector (£80 tier)
 *
 * Progress (message, track, nodes, pills) is rendered entirely server-side and
 * re-rendered on every cart change via cart-items.js -> getSectionsToRender().
 * This script only manages the £80 free-gift line item:
 *   - add the chosen variant (property _free_gift: 'true') when a radio is picked
 *   - switch to a different gift when the selection changes
 *   - remove the gift automatically if the qualifying total drops below £80
 *
 * The gift is added as a normal line carrying the Kite ("Discount & Free Gift")
 * promo properties (_free_product / _rule_id / _kite_promo_name). Kite's discount
 * function reads those and zeroes the line at checkout for the £80 "Freebie" offer
 * — we do NOT use Kite's own widget. The reward ladder threshold ignores any line
 * flagged _free_gift so the gift never inflates the customer's own spend.
 */
(function () {
  'use strict';

  // After a freebie add/swap triggers a cart-page reload, jump to the top
  // (0,0) instead of letting the browser restore the previous scroll position.
  try {
    if (sessionStorage.getItem('cpb-scroll-top') === '1') {
      sessionStorage.removeItem('cpb-scroll-top');
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      window.scrollTo(0, 0);
      window.addEventListener('load', function onLoadScrollTop() {
        window.scrollTo(0, 0);
        if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
      }, { once: true });
    }
  } catch (e) { /* noop */ }

  const routes = (window.theme && window.theme.routes) || {};
  const CART_ADD = routes.cartAdd || '/cart/add.js';
  const CART_CHANGE = routes.cartChange || '/cart/change.js';
  const CART = (routes.cart || '/cart') + '.js';

  let busy = false;

  const jsonHeaders = {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  };

  function getBar() {
    return document.getElementById('cart-progress-bar');
  }

  /** Re-render the cart surface that owns the given element (drawer or page). */
  function refreshCart(fromEl) {
    const drawerEl = fromEl && fromEl.closest ? fromEl.closest('cart-drawer') : null;
    const drawer = drawerEl || document.querySelector('cart-drawer[open]');
    if (drawer && typeof drawer.refresh === 'function') {
      drawer.refresh(true); // full drawer section re-render (includes the ladder)
      return;
    }
    const cartItems = document.getElementById('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      cartItems.refresh();
    }
  }

  async function getCart() {
    const res = await fetch(CART, { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /** Remove any line currently flagged as the free gift. */
  async function removeGiftLine(cart) {
    const data = cart || (await getCart());
    const gift = (data.items || []).find(
      (i) => i.properties && i.properties._free_gift === 'true'
    );
    if (!gift) return false;
    await fetch(CART_CHANGE, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ id: gift.key, quantity: 0 })
    });
    return true;
  }

  /**
   * Build the line-item properties Kite needs to discount the gift.
   *
   * Kite ("Discount & Free Gift", freeGift V2) matches a gift line by:
   *   _free_product === 'true'
   *   _rule_id       === the offer's per-RULE id, e.g. 'free_gift_tier_0' for the
   *                      first gift rule  (NOT the campaign _id or Shopify GID)
   *   _kite_promo_name === the offer title
   * These are the exact values observed on a line that Kite's own widget added
   * and that Shopify actually discounted to £0. We keep _free_gift too — our
   * reward-ladder math and gift add/remove logic key off it (Kite ignores it).
   */
  function giftProperties() {
    const props = {
      _free_gift: 'true', // theme flag — drives the ladder math + gift add/remove
      _free_product: 'true',
      _rule_id: 'free_gift_tier_0', // first gift rule; matches Kite's V2 discount function
      _kite_promo_name: 'Freebie'
    };
    try {
      // Keep the promo name in sync with Kite's live offer title if present.
      const campaigns =
        (window.gfg && window.gfg.settings && window.gfg.settings.freeGiftsV2) || [];
      const c = campaigns.find((x) => x && x.isEnabled) || campaigns[0];
      if (c) {
        const title = (c.discountData_shopifyResponse || {}).title || c.title;
        if (title) props._kite_promo_name = title;
      }
    } catch (e) { /* keep hard-coded values above */ }
    return props;
  }

  /** Add the chosen variant as the free gift, replacing any existing one. */
  async function setFreeGift(variantId, fromEl) {
    if (busy) return;
    busy = true;
    const bar = getBar();
    if (bar) bar.classList.add('cpb--loading');
    try {
      const cart = await getCart();
      const existing = (cart.items || []).find(
        (i) => i.properties && i.properties._free_gift === 'true'
      );
      // Already the selected gift — nothing to do.
      if (existing && String(existing.variant_id) === String(variantId)) return;

      if (existing) await removeGiftLine(cart);

      await fetch(CART_ADD, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: giftProperties() }]
        })
      });

      // On the cart PAGE, hard-reload so the new freebie line and the third-party
      // delivery-date picker re-render cleanly from the server (a partial AJAX
      // re-render wipes the app-injected picker). In the cart DRAWER keep the
      // in-place refresh — a reload would close the drawer.
      const drawerOpen = document.querySelector('cart-drawer[open]');
      if (!drawerOpen && document.querySelector('.cc-main-cart')) {
        try { sessionStorage.setItem('cpb-scroll-top', '1'); } catch (e) { /* noop */ }
        window.location.reload();
        return;
      }
      refreshCart(fromEl);
    } catch (err) {
      console.error('Reward ladder: could not set free gift', err); // eslint-disable-line no-console
    } finally {
      busy = false;
      if (bar) bar.classList.remove('cpb--loading');
    }
  }

  /**
   * Drop the gift if the customer's qualifying spend fell below the £80 tier.
   *
   * We fetch the cart fresh and compute the qualifying total ourselves rather
   * than trusting the server-rendered data-current-tier, which can still hold
   * the pre-change value when the cart-change event fires before the bar
   * re-renders. Qualifying total mirrors the Liquid: cart total minus the free
   * gift line(s), so the gift never counts toward the customer's own spend.
   */
  async function removeGiftIfBelowThreshold() {
    if (busy) return;
    const bar = getBar();
    if (!bar) return;
    const threshold = parseInt(bar.dataset.tier2Cents || '8000', 10);

    busy = true;
    try {
      const cart = await getCart();
      const items = cart.items || [];
      const giftLines = items.filter((i) => i.properties && i.properties._free_gift === 'true');
      if (!giftLines.length) return; // no gift in cart — nothing to remove

      const giftTotal = giftLines.reduce((sum, i) => sum + (i.final_line_price || 0), 0);
      const qualifying = (cart.total_price || 0) - giftTotal;
      if (qualifying >= threshold) return; // still qualifies — keep the gift

      // Below threshold: remove every free-gift line.
      for (const gift of giftLines) {
        // eslint-disable-next-line no-await-in-loop
        await fetch(CART_CHANGE, {
          method: 'POST',
          headers: jsonHeaders,
          body: JSON.stringify({ id: gift.key, quantity: 0 })
        });
      }
      refreshCart(bar);
    } catch (err) {
      console.error('Reward ladder: could not remove free gift', err); // eslint-disable-line no-console
    } finally {
      busy = false;
    }
  }

  /* ---------------------------------------------------------------------- *
   * Milestone unlock toasts (shown when the cart drawer is CLOSED)
   * ---------------------------------------------------------------------- */
  const TIER_EMOJI = { 1: '🚚', 2: '🎁', 3: '✨' };
  let lastToastTier = null;

  /** Customer's own spend = cart total minus any free-gift line(s). */
  function qualifyingTotal(cart) {
    const items = cart.items || [];
    const giftTotal = items
      .filter((i) => i.properties && i.properties._free_gift === 'true')
      .reduce((sum, i) => sum + (i.final_line_price || 0), 0);
    return (cart.total_price || 0) - giftTotal;
  }

  function tierForTotal(qualifying, bar) {
    const t1 = parseInt(bar.dataset.tier1Cents || '6000', 10);
    const t2 = parseInt(bar.dataset.tier2Cents || '8000', 10);
    const t3 = parseInt(bar.dataset.tier3Cents || '10000', 10);
    if (qualifying >= t3) return 3;
    if (qualifying >= t2) return 2;
    if (qualifying >= t1) return 1;
    return 0;
  }

  function showMilestoneToast(tier, bar) {
    const label = (bar.dataset[`tier${tier}Label`] || '').trim();
    if (!label) return;
    let toast = document.getElementById('cpb-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cpb-toast';
      toast.className = 'cpb-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }
    clearTimeout(toast._hideTimer);
    toast.innerHTML =
      `<span class="cpb-toast__icon" aria-hidden="true">${TIER_EMOJI[tier] || '🎉'}</span>`
      + `<span class="cpb-toast__text"><strong>${label}</strong> unlocked!</span>`;
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    toast._hideTimer = setTimeout(() => toast.classList.remove('is-visible'), 3500);
  }

  /** Show an unlock toast when a new tier is reached and the drawer is closed. */
  async function checkMilestoneToast() {
    const bar = getBar();
    if (!bar) return;
    let tier;
    try {
      const cart = await getCart();
      tier = tierForTotal(qualifyingTotal(cart), bar);
    } catch (e) { return; }
    const prev = lastToastTier == null ? 0 : lastToastTier;
    lastToastTier = tier;
    if (tier <= prev) return;
    if (document.querySelector('cart-drawer[open]')) return; // drawer open — bar is visible

    // Show immediately (no artificial delay). If adding to cart auto-opens the
    // drawer a moment later, dismiss the toast so both don't show at once.
    showMilestoneToast(tier, bar);
    const cancel = () => {
      const t = document.getElementById('cpb-toast');
      if (t) t.classList.remove('is-visible');
    };
    document.addEventListener('on:cart-drawer:after-open', cancel, { once: true });
    setTimeout(() => document.removeEventListener('on:cart-drawer:after-open', cancel), 1000);
  }

  /* ---------------------------------------------------------------------- *
   * £80 free-gift popup
   * ---------------------------------------------------------------------- */
  // In-memory dismissal only — deliberately NOT persisted. Closing the popup
  // suppresses it for the current view, but it re-opens on the next cart-drawer
  // visit or cart-page load so the free gift is never missed (until one is picked).
  let dismissedThisView = false;

  function getPopup() {
    return document.getElementById('free-gift-popup');
  }

  /** Mark the option matching the gift currently in the cart as selected. */
  async function markSelectedOption(popup) {
    let currentId = null;
    try {
      const cart = await getCart();
      const gift = (cart.items || []).find((i) => i.properties && i.properties._free_gift === 'true');
      if (gift) currentId = String(gift.variant_id);
    } catch (e) { /* noop */ }
    popup.querySelectorAll('.fgp__option').forEach((opt) => {
      opt.classList.toggle('is-selected', opt.dataset.fgpVariant === currentId);
    });
  }

  function openGiftPopup() {
    const popup = getPopup();
    if (!popup) return;
    popup.hidden = false;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => popup.classList.add('is-open'));
    markSelectedOption(popup);
  }

  function closeGiftPopup(dismissed) {
    const popup = getPopup();
    if (!popup) return;
    popup.classList.remove('is-open');
    document.body.style.overflow = '';
    if (dismissed) dismissedThisView = true;
    setTimeout(() => { popup.hidden = true; }, 250);
  }

  function isPopupOpen() {
    const popup = getPopup();
    return popup && !popup.hidden;
  }

  /**
   * Auto-open once the £80 tier is reached and no gift has been chosen yet.
   *
   * Computes the qualifying total from a fresh cart fetch rather than the
   * server-rendered data-current-tier, which can still hold the pre-change
   * value when this runs on cart-drawer:after-open (so the popup would wrongly
   * appear after spend has dropped below the threshold).
   */
  async function maybeAutoOpenGiftPopup() {
    const bar = getBar();
    const popup = getPopup();
    if (!bar || !popup || isPopupOpen() || dismissedThisView) return;
    const threshold = parseInt(bar.dataset.tier2Cents || '8000', 10);
    try {
      const cart = await getCart();
      const items = cart.items || [];
      const hasGift = items.some((i) => i.properties && i.properties._free_gift === 'true');
      if (hasGift) return; // already picked a gift
      if ((cart.total_price || 0) >= threshold) openGiftPopup();
    } catch (e) { /* noop */ }
  }

  // Open the popup from the progress-bar trigger ("Choose your free gift" / "Change").
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-open-gift-popup]');
    if (trigger) {
      e.preventDefault();
      dismissedThisView = false;
      openGiftPopup();
      return;
    }

    // Close controls
    if (e.target.closest && e.target.closest('[data-fgp-close]')) {
      closeGiftPopup(true);
      return;
    }
    // Click on the dimmed overlay (outside the modal) closes it
    if (e.target.classList && e.target.classList.contains('fgp-overlay')) {
      closeGiftPopup(true);
      return;
    }

    // Choosing a gift
    const option = e.target.closest && e.target.closest('.fgp__option');
    if (option && !option.disabled) {
      const popup = getPopup();
      if (popup) popup.querySelector('.fgp').classList.add('is-loading');
      setFreeGift(option.dataset.fgpVariant, option).then(() => {
        dismissedThisView = false;
        if (popup) popup.querySelector('.fgp').classList.remove('is-loading');
        closeGiftPopup(false);
      });
    }
  });

  // Esc closes the popup
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isPopupOpen()) closeGiftPopup(true);
  });

  // Re-evaluate the gift / auto-open whenever the cart changes.
  function onCartChange() {
    removeGiftIfBelowThreshold();
    maybeAutoOpenGiftPopup();
    checkMilestoneToast();
  }
  document.addEventListener('on:line-item:change', onCartChange);
  document.addEventListener('on:cart:add', onCartChange);

  // Every time the cart drawer is opened, re-prompt (reset the per-view flag)
  // so the free-gift picker keeps appearing on each cart visit until one is picked.
  document.addEventListener('on:cart-drawer:after-open', () => {
    dismissedThisView = false;
    maybeAutoOpenGiftPopup();
  });

  document.addEventListener('DOMContentLoaded', () => {
    removeGiftIfBelowThreshold();
    // Seed the toast baseline from the current tier so we only toast on the
    // NEXT increase, not for tiers already reached when the page loaded.
    const bar = getBar();
    if (bar) lastToastTier = parseInt(bar.dataset.currentTier || '0', 10);
    // On the cart page, re-prompt on every load / reload.
    if (document.querySelector('.cc-main-cart')) {
      dismissedThisView = false;
      maybeAutoOpenGiftPopup();
    }
  });
})();
